<template>
    <Teleport to="body">
        <div 
            v-if="isOpen" 
            class="fixed inset-0 z-[200] overflow-hidden bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overscroll-contain"
            @wheel.stop
            @touchmove.stop
        >
            <div 
                class="relative w-full max-w-xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 flex flex-col max-h-[85vh] overflow-hidden overscroll-contain"
                @wheel.stop
            >
                <!-- Header -->
                <div class="flex items-center justify-between p-4 md:p-5 border-b border-gray-100 dark:border-gray-700">
                    <div class="flex items-center space-x-2">
                        <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                        <h3 class="text-lg font-bold text-gray-900 dark:text-white">
                            {{ $t('schedule.eventForm.selectPlaylistsModalTitle') }}
                        </h3>
                    </div>
                    <button 
                        @click="close"
                        type="button" 
                        class="text-gray-400 hover:bg-gray-100 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-700 dark:hover:text-white transition-colors"
                    >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 14 14">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                        </svg>
                    </button>
                </div>

                <!-- Search input -->
                <div class="p-4 border-b border-gray-100 dark:border-gray-700">
                    <div class="relative">
                        <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                            </svg>
                        </div>
                        <input 
                            v-model="searchQuery"
                            type="search" 
                            class="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                            :placeholder="$t('schedule.eventForm.searchPlaylists')"
                            @input="onSearchInput"
                        />
                    </div>
                </div>

                <!-- Playlist list with infinite scroll -->
                <div 
                    ref="scrollContainer"
                    @scroll="handleScroll"
                    class="p-4 overflow-y-auto overscroll-contain flex-1 space-y-2 max-h-[50vh] min-h-[220px]"
                >
                    <div v-if="loading && playlists.length === 0" class="flex justify-center items-center py-12">
                        <Loading />
                    </div>

                    <div v-else-if="playlists.length === 0" class="text-center py-10 text-gray-500 dark:text-gray-400 text-sm">
                        {{ $t('schedule.eventForm.noPlaylistsFound') }}
                    </div>

                    <div 
                        v-for="pl in playlists" 
                        :key="pl.id || pl._id"
                        @click="togglePlaylist(pl)"
                        class="flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all duration-150"
                        :class="isSelected(pl) 
                            ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-300 dark:border-purple-600' 
                            : 'bg-white dark:bg-gray-700/40 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'"
                    >
                        <div class="flex items-center space-x-3 min-w-0 pr-3">
                            <div class="w-5 h-5 flex items-center justify-center rounded border transition-colors"
                                 :class="isSelected(pl) 
                                    ? 'bg-purple-600 border-purple-600 text-white' 
                                    : 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700'">
                                <svg v-if="isSelected(pl)" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                                </svg>
                            </div>
                            <div class="truncate">
                                <div class="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                    {{ pl.name }}
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {{ pl.songs ? pl.songs.length : 0 }} {{ $t('playlistEditor.songs') }}
                                </div>
                            </div>
                        </div>

                        <span v-if="isSelected(pl)" class="text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/50 px-2 py-0.5 rounded-full flex-shrink-0">
                            {{ $t('schedule.eventForm.selected') || 'Selected' }}
                        </span>
                    </div>

                    <!-- Bottom loader for infinite scroll -->
                    <div v-if="loadingMore" class="flex justify-center py-3">
                        <Loading />
                    </div>
                </div>

                <!-- Footer -->
                <div class="flex items-center justify-between p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-2xl">
                    <span class="text-xs text-gray-500 dark:text-gray-400">
                        {{ selectedPlaylists.length }} {{ $t('schedule.eventForm.selectedPlaylistsCount') || 'selected' }}
                    </span>
                    <button 
                        @click="confirm"
                        type="button"
                        class="px-5 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                        {{ $t('schedule.eventForm.done') || 'Done' }}
                    </button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, watch, onUnmounted, defineProps, defineEmits, defineExpose } from 'vue'
import PlaylistService from '@/service/playlist.service'
import Loading from '@/components/TriadLoad.vue'
import { debounce } from '@/service/debounce.js'

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    }
})

const emit = defineEmits(['update:modelValue', 'toggle'])

const isOpen = ref(false)
const playlists = ref([])
const selectedPlaylists = ref([])
const searchQuery = ref('')
const page = ref(1)
const totalPages = ref(1)
const loading = ref(false)
const loadingMore = ref(false)
const scrollContainer = ref(null)

watch(isOpen, (val) => {
    if (val) {
        document.body.style.overflow = 'hidden'
    } else {
        document.body.style.overflow = ''
    }
})

onUnmounted(() => {
    document.body.style.overflow = ''
})

const isSelected = (playlist) => {
    const targetId = playlist.id || playlist._id
    return selectedPlaylists.value.some(p => (p.id || p._id) === targetId)
}

const togglePlaylist = (playlist) => {
    const targetId = playlist.id || playlist._id
    if (isSelected(playlist)) {
        selectedPlaylists.value = selectedPlaylists.value.filter(p => (p.id || p._id) !== targetId)
        emit('toggle', { playlist, selected: false })
    } else {
        selectedPlaylists.value.push(playlist)
        emit('toggle', { playlist, selected: true })
    }
}

const loadPlaylists = async (isLoadMore = false) => {
    if (isLoadMore) {
        if (loadingMore.value || page.value >= totalPages.value) return
        loadingMore.value = true
        page.value += 1
    } else {
        loading.value = true
        page.value = 1
    }

    try {
        const res = await PlaylistService.fetchPaginated({
            page: page.value,
            size: 10,
            sort: '-created_at',
            search: searchQuery.value.trim()
        })

        const items = res.data || (Array.isArray(res) ? res : [])
        totalPages.value = res.totalPages || Math.ceil((res.total || items.length) / 10) || 1

        if (isLoadMore) {
            playlists.value = [...playlists.value, ...items]
        } else {
            playlists.value = items
        }
    } catch (err) {
        console.error('Error loading paginated playlists in modal:', err)
    } finally {
        loading.value = false
        loadingMore.value = false
    }
}

const onSearchInput = debounce(() => {
    loadPlaylists(false)
}, 300)

const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight - 30) {
        loadPlaylists(true)
    }
}

const open = (currentSelected = []) => {
    selectedPlaylists.value = [...(currentSelected || props.modelValue || [])]
    searchQuery.value = ''
    isOpen.value = true
    loadPlaylists(false)
}

const close = () => {
    isOpen.value = false
}

const confirm = () => {
    emit('update:modelValue', selectedPlaylists.value)
    close()
}

defineExpose({
    open,
    close
})
</script>
